import type { WorkflowActivity } from "../../../../../api/types/workflowTypes";

export interface WorkflowTokenUsage {
  groups: TokenUsageGroup[];
  totals: TokenUsageTotal[];
}

interface TokenUsageGroup {
  activityName: string;
  rows: TokenUsageRow[];
}

interface TokenUsageRow {
  agentType: "Task" | "Validation" | "Supervisor";
  inputTokens?: number;
  cachedInputTokens?: number;
  outputTokens?: number;
  modelName?: string;
  reasoningEffort?: string;
}

interface TokenUsageTotal {
  modelName: string;
  inputTokens: number;
  cachedInputTokens: number;
  outputTokens: number;
}

export const getWorkflowTokenUsage = (activities: WorkflowActivity[]): WorkflowTokenUsage => {
  const groups: TokenUsageGroup[] = activities.flatMap((activity) => {
    if (!activity.tokenUsage) {
      return [];
    }

    const activityName = activity.name;
    const attemptCount = activity.tokenUsage.attemptCount;
    const attemptSuffix = attemptCount && attemptCount > 1 ? ` (x${attemptCount})` : "";
    const activityNameWithAttempts = `${activityName}${attemptSuffix}`;
    const hasSupervisorData =
      activity.tokenUsage.supervisorAgentInputTokens !== undefined ||
      activity.tokenUsage.supervisorAgentOutputTokens !== undefined ||
      activity.tokenUsage.supervisorAgentModelName !== undefined ||
      activity.tokenUsage.supervisorAgentModelId !== undefined;

    const rows: TokenUsageRow[] = [
      {
        agentType: "Task",
        inputTokens: activity.tokenUsage.taskAgentInputTokens,
        cachedInputTokens: activity.tokenUsage.taskAgentCachedInputTokens,
        outputTokens: activity.tokenUsage.taskAgentOutputTokens,
        modelName: activity.tokenUsage.taskAgentModelName,
        reasoningEffort: activity.tokenUsage.taskAgentReasoningEffort,
      },
      {
        agentType: "Validation",
        inputTokens: activity.tokenUsage.validationAgentInputTokens,
        cachedInputTokens: activity.tokenUsage.validationAgentCachedInputTokens,
        outputTokens: activity.tokenUsage.validationAgentOutputTokens,
        modelName: activity.tokenUsage.validationAgentModelName,
        reasoningEffort: activity.tokenUsage.validationAgentReasoningEffort,
      },
    ];

    if (hasSupervisorData) {
      rows.push({
        agentType: "Supervisor",
        inputTokens: activity.tokenUsage.supervisorAgentInputTokens,
        cachedInputTokens: activity.tokenUsage.supervisorAgentCachedInputTokens,
        outputTokens: activity.tokenUsage.supervisorAgentOutputTokens,
        modelName: activity.tokenUsage.supervisorAgentModelName ?? activity.tokenUsage.supervisorAgentModelId,
        reasoningEffort: activity.tokenUsage.supervisorAgentReasoningEffort,
      });
    }

    return [
      {
        activityName: activityNameWithAttempts,
        rows,
      },
    ];
  });

  const modelTotals = new Map<string, TokenUsageTotal>();

  groups.forEach((group) => {
    group.rows.forEach((row) => {
      if (row.inputTokens === undefined && row.outputTokens === undefined && row.cachedInputTokens === undefined) {
        return;
      }

      const modelName = row.modelName ?? "Unknown Model";
      const existing = modelTotals.get(modelName) ?? {
        modelName,
        inputTokens: 0,
        cachedInputTokens: 0,
        outputTokens: 0,
      };
      existing.inputTokens += row.inputTokens ?? 0;
      existing.cachedInputTokens += row.cachedInputTokens ?? 0;
      existing.outputTokens += row.outputTokens ?? 0;
      modelTotals.set(modelName, existing);
    });
  });

  const totals = Array.from(modelTotals.values()).sort((a, b) => a.modelName.localeCompare(b.modelName));

  return {
    groups,
    totals,
  };
};
